/**
 * Tab visibility hook — thin wrapper around useAccessPolicy.
 *
 * Kept for backward compatibility: existing consumers import `useTabAccess`
 * and destructure `showDocuments`, `showPlatformAdmin`, etc.
 */
import { useAccessPolicy } from './useAccessPolicy';

export function useTabAccess() {
  const policy = useAccessPolicy();

  if (process.env.NODE_ENV !== 'production') {
    console.debug('useTabAccess', {
      planTier: policy.planTier,
      isSubscriptionActive: policy.isSubscriptionActive,
      isTrialActive: policy.isTrialActive,
      hasProgressFeature: policy.hasProgressFeature,
      isSuperAdmin: policy.isSuperAdmin,
      isSchoolAdmin: policy.isSchoolAdmin,
      isB2C: policy.isB2C,
      isMentor: policy.isMentor,
      canShare: policy.canShare,
    });
  }

  return {
    // Identity & subscription (pass-through)
    user: policy.user,
    chatTitle: policy.chatTitle,
    planTier: policy.planTier,
    subscriptionActive: policy.isSubscriptionActive,
    isSuper: policy.isSuperAdmin,
    isSchool: policy.isSchoolAdmin,
    isB2BUser: policy.isB2BUser,
    isB2C: policy.isB2C,
    isMentor: policy.isMentor,
    isMentorOrAdmin: policy.isMentorOrAdmin,
    canShare: policy.canShare,

    // Tab visibility (derived from policy capabilities)
    showDocuments: policy.canAccessDocumentsTab,                        // super admin only
    showPlatformAdmin: policy.canAccessPlatformAdmin,                   // super admin only
    showSchoolManagement: policy.isSchoolAdmin || policy.isSuperAdmin,
    showStudents: policy.canAccessAdminStudentList,                     // admin + super only (NOT mentor)
    // Active Tier 1 B2C users can open the backend-powered teaser; mentors
    // still have no personal progress surface.
    showProgress: Boolean(
      policy.user
      && !policy.isMentor
      && (policy.hasProgressFeature || (policy.isB2C && policy.isSubscriptionActive))
    ),
  };
}
